/**
 * Google Maps Script Loader Utility
 * Ensures Google Maps API is loaded only once across the application
 */

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let isLoading = false;
let isLoaded = false;
const callbacks = [];

/**
 * Load Google Maps script once
 * @param {Function} callback - Function to call when script is loaded
 */
export const loadGoogleMaps = (callback) => {
  // Already loaded
  if (isLoaded || (window.google && window.google.maps)) {
    isLoaded = true;
    if (callback) callback();
    return;
  }

  // Add callback to queue
  if (callback) {
    callbacks.push(callback);
  }

  // Already loading, just wait
  if (isLoading) {
    return;
  }

  // Check if script tag already exists
  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    // Script exists but may not be loaded yet
    existingScript.addEventListener('load', () => {
      isLoaded = true;
      callbacks.forEach(cb => cb());
      callbacks.length = 0;
    });
    return;
  }

  // Start loading
  isLoading = true;

  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,visualization&loading=async`;
  script.async = true;
  script.defer = true;
  
  script.onload = () => {
    isLoading = false;
    isLoaded = true;
    // Small delay to ensure all libraries are initialized
    setTimeout(() => {
      callbacks.forEach(cb => cb());
      callbacks.length = 0;
    }, 100);
  };

  script.onerror = () => {
    isLoading = false;
    console.error('Failed to load Google Maps script');
  };

  document.head.appendChild(script);
};

/**
 * Check if Google Maps is loaded
 */
export const isGoogleMapsLoaded = () => {
  return isLoaded || (window.google && window.google.maps);
};

/**
 * Initialize autocomplete on an input element
 * @param {HTMLElement} input - Input element
 * @param {Object} options - Autocomplete options
 * @param {Function} onPlaceChanged - Callback when place is selected
 */
export const initAutocomplete = (input, options = {}, onPlaceChanged) => {
  if (!input || !window.google || !window.google.maps) {
    console.warn('Google Maps not loaded or input not found');
    return null;
  }

  const defaultOptions = {
    componentRestrictions: { country: 'ca' },
    fields: ['formatted_address', 'geometry', 'address_components']
  };

  const autocomplete = new window.google.maps.places.Autocomplete(
    input,
    { ...defaultOptions, ...options }
  );

  if (onPlaceChanged) {
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      onPlaceChanged(place);
    });
  }

  return autocomplete;
};

export default {
  loadGoogleMaps,
  isGoogleMapsLoaded,
  initAutocomplete
};
